// Events Store to manage SSE events
import { defineStore } from 'pinia'

export interface SSEEvent {
  type: string
  data: any
  timestamp: Date
}

export const useEventsStore = defineStore('events', {
  state: () => ({
    events: [] as SSEEvent[],
    isConnected: false,
    maxEvents: 100, // Maximum number of events to store
    eventListeners: new Map<string, Array<(event: SSEEvent) => void>>(), // Event listeners by type
  }),
  getters: {
    getEvents(): SSEEvent[] {
      return this.events
    },

    getConnectionStatus(): boolean {
      return this.isConnected
    },

    getEventsByType:
      (state) =>
      (eventType: string): SSEEvent[] => {
        return state.events.filter((event) => event.type === eventType)
      },
  },

  actions: {
    addEvent(event: SSEEvent) {
      this.events.unshift(event) // Add to the beginning of the array

      // Limit the number of events to prevent memory issues
      if (this.events.length > this.maxEvents) {
        this.events = this.events.slice(0, this.maxEvents)
      }

      // Notify listeners
      this.notifyEventListeners(event)
    },

    clearEvents() {
      this.events = []
    },

    setConnectionStatus(status: boolean) {
      this.isConnected = status
    },

    /**
     * Subscribe to specific event types
     */
    addEventListener(eventType: string, callback: (event: SSEEvent) => void) {
      if (!this.eventListeners.has(eventType)) {
        this.eventListeners.set(eventType, [])
      }
      this.eventListeners.get(eventType)!.push(callback)

      // Return unsubscribe function
      return () => {
        const listeners = this.eventListeners.get(eventType)
        if (listeners) {
          const index = listeners.indexOf(callback)
          if (index > -1) {
            listeners.splice(index, 1)
          }
        }
      }
    },

    /**
     * Notify all listeners for a specific event type
     */
    notifyEventListeners(event: SSEEvent) {
      const listeners = this.eventListeners.get(event.type)
      if (listeners) {
        listeners.forEach((callback) => {
          try {
            callback(event)
          } catch (error) {
            console.error(`Error in event listener for ${event.type}:`, error)
          }
        })
      }
    },
  },
})
